/**
 * Hybrid Event Types for Web UI
 *
 * Type definitions for hybrid events used in the Vibe Remote web UI.
 * These types mirror the Zod schemas in the relay server and Pydantic models
 * in the daemon for type-safe event handling.
 *
 * Event Categories:
 * - control:* - User control events (messages, settings, permissions, interrupts)
 * - stream:* - Streaming output events (frames, chunks with sequencing)
 * - visual:* - Visual parity events (tool lifecycle, agent lifecycle, progress)
 * - state:* - Session state change events
 *
 * Phase: Event Schema Evolution
 */

// =============================================================================
// Enums
// =============================================================================

/**
 * Session state enumeration
 *
 * State machine:
 * - idle: Session is ready for user input
 * - thinking: Claude is processing/generating response
 * - tooling: Claude is executing a tool
 * - waiting_for_user: Claude is waiting for user response (e.g., permission)
 * - done: Session turn is complete
 * - error: Session encountered an error
 */
export type SessionState =
  | 'idle'
  | 'thinking'
  | 'tooling'
  | 'waiting_for_user'
  | 'done'
  | 'error';

export type PermissionMode =
  | 'default'
  | 'accept-edits'
  | 'plan-mode'
  | 'yolo-mode';

export type ModelOption =
  | 'cli-settings'
  | 'opus-4-5'
  | 'sonnet-4-5'
  | 'haiku-4-5'
  | 'opus-then-sonnet';

export type OperationType =
  | 'file_search'
  | 'code_analysis'
  | 'batch_edit'
  | 'test_run'
  | 'build'
  | 'deployment'
  | 'unknown';

export type PermissionType =
  | 'edit'
  | 'bash'
  | 'mcp'
  | 'read'
  | 'write'
  | 'execute';

export type PermissionAction =
  | 'approve'
  | 'deny'
  | 'approve_all'
  | 'deny_all';

export type InterruptType = 'soft' | 'hard';

export type FrameType = 'assistant' | 'system' | 'error' | 'debug';

export type AttachmentType = 'file' | 'image' | 'screenshot';

export type AttachmentStatusType = 'uploading' | 'processing' | 'ready' | 'failed';

export type ChangeType = 'add' | 'update' | 'remove' | 'reorder' | 'full_sync';

// =============================================================================
// Event Names
// =============================================================================

export const HybridEventNames = {
  // Control events
  CONTROL_MESSAGE: 'control:message',
  CONTROL_SETTINGS: 'control:settings',
  CONTROL_PERMISSION: 'control:permission',
  CONTROL_INTERRUPT: 'control:interrupt',
  CONTROL_SLASH_COMMAND: 'control:slash_command',

  // Stream events
  STREAM_FRAME: 'stream:frame',
  STREAM_CHUNK: 'stream:chunk',

  // Visual events
  VISUAL_TOOL_START: 'visual:tool_start',
  VISUAL_TOOL_END: 'visual:tool_end',
  VISUAL_AGENT_SPAWN: 'visual:agent_spawn',
  VISUAL_AGENT_UPDATE: 'visual:agent_update',
  VISUAL_AGENT_COMPLETE: 'visual:agent_complete',
  VISUAL_PERMISSION_REQUEST: 'visual:permission_request',
  VISUAL_PROGRESS: 'visual:progress',
  VISUAL_ATTACHMENT_STATUS: 'visual:attachment_status',

  // State events
  STATE_UPDATE: 'state:update',
} as const;

export type HybridEventName = (typeof HybridEventNames)[keyof typeof HybridEventNames];

// =============================================================================
// Base Event Interface
// =============================================================================

export interface BaseEvent {
  sessionId: string;
  timestamp: number;
  seq?: number;
}

// =============================================================================
// Control Events (Web UI -> Relay -> Daemon)
// =============================================================================

export interface AttachmentData {
  id: string;
  type: AttachmentType;
  name: string;
  size: number;
  mimeType?: string;
  url?: string;
  base64?: string;
}

export interface ControlMessage extends BaseEvent {
  eventType: 'control:message';
  content: string;
  attachments?: AttachmentData[];
  requestId?: string;
}

export interface ControlSettings extends BaseEvent {
  eventType: 'control:settings';
  permissionMode?: PermissionMode;
  modelOption?: ModelOption;
  requestId?: string;
}

export interface ControlPermission extends BaseEvent {
  eventType: 'control:permission';
  permissionId: string;
  action: PermissionAction;
  requestId?: string;
}

export interface ControlInterrupt extends BaseEvent {
  eventType: 'control:interrupt';
  interruptType: InterruptType;
  reason?: string;
  requestId?: string;
}

export interface ControlSlashCommand extends BaseEvent {
  eventType: 'control:slash_command';
  command: string;
  args?: string;
  requestId?: string;
}

export type ControlEvent =
  | ControlMessage
  | ControlSettings
  | ControlPermission
  | ControlInterrupt
  | ControlSlashCommand;

// =============================================================================
// Stream Events (Daemon -> Relay -> Web UI)
// =============================================================================

export interface StreamFrame extends BaseEvent {
  eventType: 'stream:frame';
  content: string;
  frameType: FrameType;
  isFinal: boolean;
  tokenCount?: number;
}

export interface StreamChunk extends BaseEvent {
  eventType: 'stream:chunk';
  content: string;
  chunkIndex: number;
  totalChunks?: number;
  isFinal: boolean;
}

export type StreamEvent = StreamFrame | StreamChunk;

// =============================================================================
// Visual Events (Daemon -> Relay -> Web UI)
// =============================================================================

export interface VisualToolStart extends BaseEvent {
  eventType: 'visual:tool_start';
  toolId: string;
  toolName: string;
  startTime: number;
  args?: Record<string, unknown>;
}

export interface VisualToolEnd extends BaseEvent {
  eventType: 'visual:tool_end';
  toolId: string;
  toolName: string;
  endTime: number;
  durationMs: number;
  success: boolean;
  error?: string;
  result?: unknown;
}

export interface VisualAgentSpawn extends BaseEvent {
  eventType: 'visual:agent_spawn';
  agentId: string;
  spawnTime: number;
  parentAgentId?: string;
  taskDescription?: string;
  agentName?: string;
  estimatedTokens?: number;
}

export interface VisualAgentUpdate extends BaseEvent {
  eventType: 'visual:agent_update';
  agentId: string;
  status?: string;
  tokensUsed?: number;
  progressPercentage?: number;
  currentAction?: string;
}

export interface VisualAgentComplete extends BaseEvent {
  eventType: 'visual:agent_complete';
  agentId: string;
  endTime: number;
  durationMs: number;
  success: boolean;
  tokensUsed: number;
  error?: string;
  result?: string;
}

export interface VisualPermissionRequest extends BaseEvent {
  eventType: 'visual:permission_request';
  permissionId: string;
  permissionType: PermissionType;
  resource: string;
  description?: string;
  preview?: string;
  timeoutMs?: number;
}

export interface VisualProgress extends BaseEvent {
  eventType: 'visual:progress';
  operationType: OperationType;
  progressPercentage: number;
  currentItem?: string;
  totalItems?: number;
  completedItems?: number;
  estimatedRemainingMs?: number;
}

export interface AttachmentStatusData {
  id: string;
  name: string;
  status: AttachmentStatusType;
  progress?: number;
  error?: string;
}

export interface VisualAttachmentStatus extends BaseEvent {
  eventType: 'visual:attachment_status';
  attachments: AttachmentStatusData[];
}

export type VisualEvent =
  | VisualToolStart
  | VisualToolEnd
  | VisualAgentSpawn
  | VisualAgentUpdate
  | VisualAgentComplete
  | VisualPermissionRequest
  | VisualProgress
  | VisualAttachmentStatus;

// =============================================================================
// State Events (Daemon -> Relay -> Web UI)
// =============================================================================

export interface StateUpdate extends BaseEvent {
  eventType: 'state:update';
  state: SessionState;
  previousState?: SessionState;
  changeType?: string;
  isRunning?: boolean;
  exitCode?: number;
  lastError?: string;
  lastActivity?: number;
}

export type StateEvent = StateUpdate;

// =============================================================================
// Combined Types
// =============================================================================

export type HybridEvent =
  | ControlEvent
  | StreamEvent
  | VisualEvent
  | StateEvent;

// =============================================================================
// Type Guards
// =============================================================================

export function isControlEvent(event: HybridEvent): event is ControlEvent {
  return event.eventType.startsWith('control:');
}

export function isStreamEvent(event: HybridEvent): event is StreamEvent {
  return event.eventType.startsWith('stream:');
}

export function isVisualEvent(event: HybridEvent): event is VisualEvent {
  return event.eventType.startsWith('visual:');
}

export function isStateEvent(event: HybridEvent): event is StateEvent {
  return event.eventType.startsWith('state:');
}

// Specific event type guards
export function isControlMessage(event: HybridEvent): event is ControlMessage {
  return event.eventType === 'control:message';
}

export function isVisualToolStart(event: HybridEvent): event is VisualToolStart {
  return event.eventType === 'visual:tool_start';
}

export function isVisualToolEnd(event: HybridEvent): event is VisualToolEnd {
  return event.eventType === 'visual:tool_end';
}

export function isVisualAgentSpawn(event: HybridEvent): event is VisualAgentSpawn {
  return event.eventType === 'visual:agent_spawn';
}

export function isVisualAgentComplete(event: HybridEvent): event is VisualAgentComplete {
  return event.eventType === 'visual:agent_complete';
}

export function isVisualPermissionRequest(event: HybridEvent): event is VisualPermissionRequest {
  return event.eventType === 'visual:permission_request';
}

export function isStateUpdate(event: HybridEvent): event is StateUpdate {
  return event.eventType === 'state:update';
}

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Create a base event with default values
 */
export function createBaseEvent(sessionId: string, seq?: number): BaseEvent {
  return {
    sessionId,
    timestamp: Date.now() / 1000,
    seq,
  };
}

/**
 * Create a control:message event
 */
export function createControlMessage(
  sessionId: string,
  content: string,
  options?: {
    attachments?: AttachmentData[];
    requestId?: string;
    seq?: number;
  }
): ControlMessage {
  return {
    ...createBaseEvent(sessionId, options?.seq),
    eventType: 'control:message',
    content,
    attachments: options?.attachments,
    requestId: options?.requestId,
  };
}

/**
 * Create a control:interrupt event
 */
export function createControlInterrupt(
  sessionId: string,
  interruptType: InterruptType = 'soft',
  options?: {
    reason?: string;
    requestId?: string;
    seq?: number;
  }
): ControlInterrupt {
  return {
    ...createBaseEvent(sessionId, options?.seq),
    eventType: 'control:interrupt',
    interruptType,
    reason: options?.reason,
    requestId: options?.requestId,
  };
}

/**
 * Create a control:permission event
 */
export function createControlPermission(
  sessionId: string,
  permissionId: string,
  action: PermissionAction,
  options?: {
    requestId?: string;
    seq?: number;
  }
): ControlPermission {
  return {
    ...createBaseEvent(sessionId, options?.seq),
    eventType: 'control:permission',
    permissionId,
    action,
    requestId: options?.requestId,
  };
}

// =============================================================================
// State Transition Validation
// =============================================================================

const VALID_TRANSITIONS: Record<SessionState, SessionState[]> = {
  idle: ['thinking', 'error'],
  thinking: ['tooling', 'waiting_for_user', 'done', 'error', 'idle'],
  tooling: ['thinking', 'done', 'error'],
  waiting_for_user: ['thinking', 'done', 'error', 'idle'],
  done: ['idle', 'error'],
  error: ['idle'],
};

/**
 * Check if a state transition is valid
 */
export function isValidStateTransition(from: SessionState, to: SessionState): boolean {
  return VALID_TRANSITIONS[from]?.includes(to) ?? false;
}

/**
 * Get valid next states from current state
 */
export function getValidNextStates(currentState: SessionState): SessionState[] {
  return VALID_TRANSITIONS[currentState] ?? [];
}

// =============================================================================
// Chunk Buffer for Stream Reassembly
// =============================================================================

export class ClientChunkBuffer {
  private chunks: Map<number, string> = new Map();
  private expectedTotal: number | undefined;
  private readonly sessionId: string;

  constructor(sessionId: string) {
    this.sessionId = sessionId;
  }

  /**
   * Add a chunk to the buffer
   * @returns true if all chunks are received
   */
  addChunk(chunk: StreamChunk): boolean {
    if (chunk.sessionId !== this.sessionId) {
      throw new Error(`Session ID mismatch: expected ${this.sessionId}, got ${chunk.sessionId}`);
    }

    this.chunks.set(chunk.chunkIndex, chunk.content);

    if (chunk.totalChunks !== undefined) {
      this.expectedTotal = chunk.totalChunks;
    }

    if (chunk.isFinal && this.expectedTotal === undefined) {
      this.expectedTotal = chunk.chunkIndex + 1;
    }

    return this.isComplete();
  }

  isComplete(): boolean {
    if (this.expectedTotal === undefined) {
      return false;
    }
    return this.chunks.size === this.expectedTotal;
  }

  assemble(): string {
    if (!this.isComplete()) {
      throw new Error('Buffer is not complete');
    }

    const sortedIndices = [...this.chunks.keys()].sort((a, b) => a - b);
    return sortedIndices.map((i) => this.chunks.get(i)!).join('');
  }

  clear(): void {
    this.chunks.clear();
    this.expectedTotal = undefined;
  }
}
